import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, CalendarBlank, ChartLineUp, CheckSquare, Hammer, List, Plus, Rocket, Table,
} from "@phosphor-icons/react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, StateBadge } from "@/components/shared";
import { CommitmentsPanel } from "@/pages/panchatanthra/CommitmentsPanel";
import { BmWeeklyReview } from "@/pages/panchatanthra/BmWeeklyReview";
import { MonthlySummary } from "@/pages/panchatanthra/MonthlySummary";
import { TrendPanel } from "@/pages/panchatanthra/TrendPanel";
import { SltRespondents } from "@/pages/panchatanthra/SltRespondents";
import { fmtPeriod } from "@/lib/period";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";


const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"];
const isoMonth = (label) => {
  const [name, year] = String(label).split(" ");
  const index = MONTH_NAMES.indexOf(name);
  return index === -1 ? label : `${year}-${String(index + 1).padStart(2, "0")}`;
};

const REVIEW_PREFIX = { REV: "RM", FIN: "FP", LEAD: "LE", BMOD: "BM", TRUST: "TR" };

// elements answered by several named SLT members instead of a single login
const MULTI_RESPONDENT = ["LEAD"];

const sectionsFor = (code, framework) => [
  { key: "workshops", label: "Workshop status", icon: Hammer },
  { key: "assessment", label: "Start assessment", icon: Rocket },
  ...(framework === "panchatanthra" && code === "FIN"
    ? [{ key: "engine", label: "Financial engine (Level 3)", icon: Table,
         to: "/panchatanthra/financial-engine" }]
    : []),
  { key: "monthly", label: "Month wise summary", icon: CalendarBlank },
  { key: "overall", label: "Overall summary", icon: ChartLineUp },
  { key: "commitments", label: "Commitments", icon: CheckSquare },
  ...(framework === "panchatanthra" || code === "LEC"
    ? [{ key: "reviews", icon: CalendarBlank,
         label: `${REVIEW_PREFIX[code] ? `${REVIEW_PREFIX[code]} weekly` : "Weekly"} review` }]
    : []),
];

const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August",
  "September", "October", "November", "December"].map((m) => `${m} 2026`);

export default function LegacySystem() {
  const { framework: fwParam, code: codeParam } = useParams();
  const FW = fwParam || "viveka_kartha";
  const BASE_CODE = codeParam || "LEC";
  const { tenantId, can } = useAuth();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const section = params.get("section") || "workshops";
  const [data, setData] = useState(null);
  const [navOpen, setNavOpen] = useState(true);
  const [month, setMonth] = useState(MONTHS[5]);
  const [ws, setWs] = useState({ name: "", link: "", workshop_date: "", remarks: "" });
  const [wsOpen, setWsOpen] = useState(false);
  const SECTIONS = sectionsFor(BASE_CODE, FW);

  const base = `/tenants/${tenantId}/frameworks/${FW}/elements/${BASE_CODE}`;
  const load = useCallback(async () => {
    if (!tenantId) return;
    try {
      setData((await api.get(base)).data);
    } catch (e) {
      setData({ error: errMsg(e) });
    }
  }, [base, tenantId]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const addWorkshop = async () => {
    try {
      await api.post(`${base}/workshops`, ws);
      setWsOpen(false);
      setWs({ name: "", link: "", workshop_date: "", remarks: "" });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const startAssessment = async () => {
    try {
      await api.post(`${base}/assessments`, { month, status: "in_progress" });
      navigate(`/survey/${FW}?period=${isoMonth(month)}${BASE_CODE === "LEC" ? "" : `&element=${BASE_CODE}`}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!data) return <p className="label-caps animate-pulse" data-testid="lec-loading">Loading legacy system</p>;
  if (data.error) return <EmptyState testid="lec-error" title="Legacy system unavailable" message={data.error} />;

  return (
    <div className="space-y-6" data-testid="vk-legacy-system">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Link to={FW === "panchatanthra" ? "/panchatanthra" : "/viveka-kartha"} data-testid="lec-back"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.16em] text-navy hover:text-deepblue">
          <span className="grid h-7 w-7 place-items-center rounded-full bg-navy text-white"><ArrowLeft size={13} /></span>
          Back to framework
        </Link>
        <button data-testid="lec-nav-toggle" onClick={() => setNavOpen((v) => !v)}
                className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-[10px] uppercase tracking-[0.14em] text-navy transition-colors hover:bg-navy hover:text-white">
          <List size={13} /> {navOpen ? "Hide panel" : "Show panel"}
        </button>
      </div>

      <div className={`grid gap-6 ${navOpen ? "lg:grid-cols-[16rem_1fr]" : "grid-cols-1"}`}>
        {navOpen && (
          <aside className="panel h-fit overflow-hidden" data-testid="lec-side-panel">
            <div className="glass-dark grain-dark relative overflow-hidden rounded-t-xl bg-navy px-5 py-7 text-white">
              <p className="font-display text-lg leading-snug text-[hsl(288_70%_82%)]">
                {/asset creation system/i.test(data.element.name)
                  ? data.element.name
                  : `${data.element.name} Asset creation System`}
              </p>
            </div>
            <nav className="flex flex-col p-3">
              {SECTIONS.map((s) => (
                s.to ? (
                  <Link key={s.key} to={s.to} data-testid={`lec-nav-${s.key}`}
                        className="flex items-center gap-3 rounded-lg px-3 py-3 text-left text-xs text-terracotta transition-colors hover:bg-terracotta hover:text-white">
                    <s.icon size={16} /> {s.label}
                  </Link>
                ) : (
                <button key={s.key} data-testid={`lec-nav-${s.key}`}
                        onClick={() => setParams({ section: s.key })}
                        className={`flex items-center gap-3 rounded-lg px-3 py-3 text-left text-xs transition-colors ${
                          section === s.key ? "bg-navy text-white" : "text-navy hover:bg-muted"}`}>
                  <s.icon size={16} /> {s.label}
                </button>
                )
              ))}
            </nav>
          </aside>
        )}

        <div className="space-y-6">
          {section === "workshops" && (
            <section className="panel overflow-hidden" data-testid="lec-workshops">
              <div className="flex items-center justify-between border-b border-border px-6 py-5">
                <h1 className="font-display text-2xl text-navy">Workshop status</h1>
                {can("create") && (
                  <Dialog open={wsOpen} onOpenChange={setWsOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="lec-add-workshop" className="h-9 rounded-full text-[10px] uppercase tracking-[0.16em]">
                        <Plus size={13} className="mr-1.5" /> Add workshop
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader><DialogTitle className="font-display text-2xl">New workshop</DialogTitle></DialogHeader>
                      <div className="space-y-4">
                        {[["Workshop name", "name"], ["Workbook link (optional)", "link"], ["Remarks", "remarks"]].map(([label, key]) => (
                          <div key={key} className="space-y-2">
                            <Label className="label-caps">{label}</Label>
                            <Input data-testid={`lec-workshop-${key}`} value={ws[key]}
                                   onChange={(e) => setWs({ ...ws, [key]: e.target.value })} />
                          </div>
                        ))}
                        <div className="space-y-2">
                          <Label className="label-caps">Workshop date</Label>
                          <Input type="date" data-testid="lec-workshop-date" value={ws.workshop_date}
                                 onChange={(e) => setWs({ ...ws, workshop_date: e.target.value })} />
                        </div>
                        <Button data-testid="lec-workshop-submit" className="w-full" disabled={!ws.name}
                                onClick={addWorkshop}>Add workshop</Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </div>
              <div className="hidden grid-cols-[3rem_2fr_1fr_1fr_0.9fr_1.2fr] gap-3 border-b border-border px-6 py-3 text-[9px] uppercase tracking-[0.12em] text-muted-foreground md:grid">
                <span>Sl no</span><span>Workshop name</span><span>Workbook</span><span>Status</span><span>Date</span><span>Remarks</span>
              </div>
              <div className="divide-y divide-border">
                {data.workshops.map((w, i) => (
                  <div key={w.id} data-testid={`lec-workshop-row-${w.id}`}
                       className="grid grid-cols-1 gap-2 px-6 py-4 transition-colors hover:bg-muted/50 md:grid-cols-[3rem_2fr_1fr_1fr_0.9fr_1.2fr] md:items-center md:gap-3">
                    <span className="font-mono text-xs text-muted-foreground">{i + 1}</span>
                    <span className="text-sm font-medium text-navy">{w.name}</span>
                    {w.link
                      ? <a href={w.link} target="_blank" rel="noreferrer"
                           className="text-[10px] uppercase tracking-[0.14em] text-deepblue hover:underline">View</a>
                      : <span className="text-xs text-muted-foreground">—</span>}
                    <Select value={w.status}
                            onValueChange={async (v) => {
                              await api.put(`${base}/workshops/${w.id}`, { status: v }).catch((e) => toast.error(errMsg(e)));
                              load();
                            }}>
                      <SelectTrigger data-testid={`lec-workshop-status-${w.id}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["not_started", "in_progress", "completed"].map((s) => (
                          <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <span className="font-mono text-xs text-muted-foreground">{w.workshop_date || "—"}</span>
                    <span className="text-xs text-foreground/70">{w.remarks || "—"}</span>
                  </div>
                ))}
                {data.workshops.length === 0 && (
                  <EmptyState testid="lec-workshops-empty" title="No workshops yet"
                              message="Add the legacy workshops delivered for this family." />
                )}
              </div>
            </section>
          )}

          {section === "assessment" && (
            <section className="panel p-6 md:p-8" data-testid="lec-assessment">
              <h1 className="font-display text-2xl text-navy">Start assessment</h1>
              <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-[1fr_1fr_auto] md:items-end">
                <div className="space-y-2">
                  <Label className="label-caps">Survey month</Label>
                  <Select value={month} onValueChange={setMonth}>
                    <SelectTrigger data-testid="lec-month-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{MONTHS.map((m) => <SelectItem key={m} value={m}>{fmtPeriod(m)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="label-caps">Status</Label>
                  <div data-testid="lec-assessment-status"
                       className="flex h-10 items-center rounded-md border border-border bg-muted/40 px-3 text-sm text-navy">
                    {data.assessments.find((a) => a.month === month)?.status?.replace("_", " ") || "not started"}
                  </div>
                </div>
                {!MULTI_RESPONDENT.includes(BASE_CODE) && (
                  <Button data-testid="lec-start-assessment" onClick={startAssessment}
                          className="h-10 rounded-full text-[10px] uppercase tracking-[0.16em]">Start assessment</Button>
                )}
              </div>
              {MULTI_RESPONDENT.includes(BASE_CODE) ? (
                <div className="mt-8 border-t border-border pt-7">
                  <SltRespondents
                    framework={FW}
                    element={BASE_CODE}
                    period={isoMonth(month)}
                    monthLabel={fmtPeriod(month)}
                    onPick={async (key) => {
                      await api.post(`${base}/assessments`, { month, status: "in_progress" })
                        .catch(() => {});
                      navigate(`/survey/${FW}?period=${isoMonth(month)}&element=${BASE_CODE}&respondent=${key}`);
                    }}
                  />
                </div>
              ) : (
                <p className="mt-6 text-xs text-muted-foreground">
                  Answers are recorded against {fmtPeriod(month)} and feed that month's summary and the
                  overall trend.
                </p>
              )}
            </section>
          )}

          {section === "monthly" && (
            <MonthlySummary base={base} elementName={data.element.name} />
          )}

          {section === "overall" && (
            <TrendPanel trend={data.trend} subScores={data.sub_scores10} score10={data.score10} />
          )}

          {section === "reviews" && (
            <BmWeeklyReview base={base} can={can}
                            title={`${REVIEW_PREFIX[BASE_CODE] ? `${REVIEW_PREFIX[BASE_CODE]} weekly` : "Weekly"} review`}
                            elementName={data.element.name} />
          )}

          {section === "commitments" && (
            <CommitmentsPanel base={base} tenantId={tenantId} framework={FW}
                              elementCode={BASE_CODE} commitments={data.commitments}
                              reload={load} can={can} />
          )}
        </div>
      </div>
    </div>
  );
}
